import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  useGetListObjectivesQuery,
  usePostGenObjectivesByAIMutation,
} from "../../../services/apiSlice";
import { ObjectiveOption } from "../../../components/Quest/ObjectivesOption";
import images from "../../../images";
import { useNavigate, useLocation } from "react-router-dom";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const StepObjectives = ({
  isModalOpen = false,
  onObjectiveSelect,
  selectedObjectiveName,
  currentStep,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const defaultQuestionModalRef = useRef();
  const { objectivesDataRedux, objectivesDataReduxManualInput } = useSelector(
    (state) => state.common
  );
  const { objectiveDataAIRedux } = useSelector((state) => state.objectiveAI);

  // Initialize limit with a fallback to 4
  const initialLimit = location.state?.limit || 4;

  const [state, setState] = useState({
    page: 1,
    limit: initialLimit,
    status: "",
    selectedObjective: null,
  });

  const {
    data: objectivesData,
    isLoading: isLoadingObjectiveData,
    refetch: refetchObjectives,
  } = useGetListObjectivesQuery({
    page: state.page,
    limit: state.limit,
    status: state.status,
  });

  const [
    postGenObjectivesByAIMutation,
    { isLoading: isLoadingAIGenObjective },
  ] = usePostGenObjectivesByAIMutation();

  // Refetch only when explicitly needed, not on limit change
  useEffect(() => {
    if (currentStep === 1) {
      setState((prev) => ({
        ...prev,
        page: 1,
        limit: location.state?.limit || 4,
        status: "",
        selectedObjective: null,
      }));
      refetchObjectives();
    }
  }, [currentStep, location.state?.limit, refetchObjectives]);

  // Memoized handlers
  const handleObjectiveSelect = useCallback(
    (id) => {
      const findObjective = (array) =>
        array?.find((obj) => obj.id === id) || null;

      const selectedObjective =
        findObjective(objectivesDataReduxManualInput) ||
        findObjective(objectiveDataAIRedux) ||
        findObjective(objectivesDataRedux);

      const objectiveName = selectedObjective?.name ?? "";
      const isCustomManual = !!(
        selectedObjective &&
        (objectivesDataReduxManualInput?.includes(selectedObjective) ||
          objectiveDataAIRedux?.includes(selectedObjective))
      );
      const isDefaultObjective = !!(
        selectedObjective && objectivesDataRedux?.includes(selectedObjective)
      );

      setState((prev) => ({ ...prev, selectedObjective: id }));

      if (onObjectiveSelect) {
        const payload = isCustomManual
          ? { id, name: objectiveName, status: "customManual" }
          : isDefaultObjective
          ? { id, name: objectiveName, idObjectiveDefault: id }
          : { id, name: objectiveName };
        setTimeout(() => onObjectiveSelect(payload), 0);
      }
    },
    [
      objectivesDataRedux,
      objectivesDataReduxManualInput,
      objectiveDataAIRedux,
      onObjectiveSelect,
    ]
  );

  const handleEditObjective = useCallback(
    (objective) => {
      navigate("/ondemand/add-objective", {
        state: { objectiveToEdit: objective, limit: state.limit },
      });
    },
    [navigate, state.limit]
  );

  // Effects
  useEffect(() => {
    if (!defaultQuestionModalRef.current) return;
    isModalOpen
      ? defaultQuestionModalRef.current.openModal()
      : defaultQuestionModalRef.current.closeModal();
  }, [isModalOpen]);

  useEffect(() => {
    if (state.selectedObjective) {
      handleObjectiveSelect(state.selectedObjective);
    }
  }, [state.selectedObjective, handleObjectiveSelect]);

  useEffect(() => {
    if (state.selectedObjective !== null) return;

    const findDefaultId = () => {
      if (selectedObjectiveName) {
        return (
          objectivesDataReduxManualInput?.find(
            (obj) => obj.name === selectedObjectiveName
          )?.id ||
          objectivesDataRedux?.find((obj) => obj.name === selectedObjectiveName)
            ?.id
        );
      }
      return (
        objectivesDataReduxManualInput?.[
          objectivesDataReduxManualInput.length - 1
        ]?.id || objectivesDataRedux?.[0]?.id
      );
    };

    const defaultId = findDefaultId();
    if (defaultId) {
      setState((prev) => ({ ...prev, selectedObjective: defaultId }));
    }
  }, [
    objectivesDataReduxManualInput,
    objectivesDataRedux,
    selectedObjectiveName,
    state.selectedObjective,
  ]);

  // Combine all objectives into a single array: custom (reversed), default, AI-generated
  const allObjectives = [
    ...(objectivesDataReduxManualInput?.slice().reverse() || []), // Custom objectives at top, latest first
    ...(objectivesDataRedux || []), // Default objectives in middle
    ...(objectiveDataAIRedux || []), // AI-generated objectives at bottom
  ].filter(
    (obj, index, self) => index === self.findIndex((t) => t.id === obj.id)
  ); // Remove duplicates by id

  const handleGenerateMoreObjectives = useCallback(async () => {
    if (isLoadingObjectiveData || isLoadingAIGenObjective) {
      return;
    }

    try {
      // Prepare existing objectives to avoid duplicates
      const body = {
        objective_exist: allObjectives?.map((obj) => obj.name) || [],
      };

      // Call AI generation API if no more default objectives or limit reached
      if (
        !objectivesData ||
        objectivesData?.total === undefined ||
        objectivesDataRedux?.length >= objectivesData?.total
      ) {
        await postGenObjectivesByAIMutation(body).unwrap();
      }

      // Refetch objectives to include new AI-generated ones
      await refetchObjectives();

      // Increment limit to fetch more objectives
      setState((prev) => ({
        ...prev,
        limit: prev.limit + 2,
      }));
    } catch (error) {
      console.error("Error in handleGenerateMoreObjectives:", error);
    }
  }, [
    objectivesDataRedux,
    objectivesData,
    postGenObjectivesByAIMutation,
    refetchObjectives,
    isLoadingObjectiveData,
    isLoadingAIGenObjective,
    allObjectives,
  ]);

  // Render objectives
  const renderObjectives = (objectives) =>
    objectives?.map((objective) => (
      <ObjectiveOption
        key={objective.id}
        text={objective.name}
        isSelected={state.selectedObjective === objective.id}
        onClick={() => handleObjectiveSelect(objective.id)}
        handleEditObjective={
          objectivesDataReduxManualInput?.some((o) => o.id === objective.id)
            ? () => handleEditObjective(objective)
            : undefined
        }
        showEditButton={objectivesDataReduxManualInput?.some(
          (o) => o.id === objective.id
        )}
      />
    ));

  const isLoading = isLoadingObjectiveData || isLoadingAIGenObjective;
  const showCustomButton = objectivesDataReduxManualInput?.length !== 1;

  return (
    <>
      <LoadingCommon statusLoading={isLoading} />
      <section className="flex flex-col gap-4 p-3 bg-white rounded-xl w-[396px] mx-auto max-md:my-0 max-md:w-full max-md:max-w-[396px] max-sm:p-2 mb-4">
        <h2 className="mb-4 text-lg text-zinc-800">
          {t("quest.stepObjective.selectOneObjective")}
        </h2>
        {renderObjectives(allObjectives)}
        <button
          onClick={handleGenerateMoreObjectives}
          disabled={isLoading}
          className="flex justify-between items-center gap-3 px-6 py-4 rounded-full text-white text-sm font-medium bg-gradient-to-r from-green-400 to-blue-500 disabled:opacity-50"
        >
          <img
            src={images.GenAIIconWhite}
            alt="Generate"
            className="w-4 h-4 mr-2"
          />
          <span>{t("quest.stepObjective.generateObjective")}</span>
          <div />
        </button>

        {showCustomButton && (
          <button
            onClick={() =>
              navigate("/ondemand/add-objective", {
                state: { limit: state.limit },
              })
            }
            className="flex items-center justify-between w-full rounded-full px-6 py-4 text-indigo-600 text-sm bg-gray-200"
          >
            <img
              src={images.PencilIconBlue}
              alt="Manual"
              className="w-4 h-4 mr-2"
            />
            <span>{t("quest.stepObjective.customObjective")}</span>
            <div />
          </button>
        )}
      </section>
    </>
  );
};

export default StepObjectives;
